// types
import {
  DispatcherRoutes,
  EaiBalanceGroupRoutes,
  EaiDeliveryContractsRoutes,
  EaiDeliveryPointsRoutes,
  EaiDevicesRoutes,
  EaiMemberAccountRoutes,
  EaiMemberAccountUserRoutes,
  EaiSubjectsRoutes,
  EaiUsersRoutes,
  ReportingRoutes,
  SystemRoutes
} from 'models/routes';

import {
  DispatcherRkCA,
  DispatcherAccessPageCA,
  DispatcherOperationCalendarCA,
  DispatcherProductionPlanningCA,
  EaiAccessPageCA,
  EaiBalanceGroupCA,
  EaiDeliveryContractsCA,
  EaiDeliveryPointsCA,
  EaiDevicesCA,
  EaiSubjectsCA,
  EaiUsersCA,
  ReportingCA
} from 'models/contextActions';
import { AnyParams, KeyedObject } from 'models';

// ==============================|| CONSTANTS ||============================== //

export enum CountryCode {
  SVK = 'SVK'
}

export enum AppModule {
  DISPATCHER = 'dispatcher',
  REPORTING = 'reporting',
  EAI = 'eai',
  HOMEPAGE = 'homepage',
  SYSTEM = 'system'
}

export const Routes = {
  BASE_PATH: '/',
  DISPATCHER: DispatcherRoutes,
  REPORTING: ReportingRoutes,
  EAI: {
    ...EaiUsersRoutes,
    ...EaiMemberAccountRoutes,
    ...EaiMemberAccountUserRoutes,
    ...EaiSubjectsRoutes,
    ...EaiBalanceGroupRoutes,
    ...EaiDeliveryPointsRoutes,
    ...EaiDevicesRoutes,
    ...EaiDeliveryContractsRoutes
  },
  SYSTEM: SystemRoutes
};

export enum EMSMemberAccountStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BLOCKED = 'BLOCKED'
}

export enum EMSMemberAccountType {
  MARKET_PARTICIPANT = 'MARKET_PARTICIPANT',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER'
}

export enum EMSPartyType {
  ORGANISATION = 'ORGANISATION',
  INDIVIDUAL = 'INDIVIDUAL'
}

export enum InputType {
  TEXT_FIELD = 'text',
  NUMBER = 'number',
  RADIO_GROUP = 'radio',
  SELECT = 'select',
  PASSWORD = 'password',
  FILE = 'file',
  DATE = 'date',
  SWITCH = 'switch'
}

export enum YupType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean'
}

export enum AppStorageKey {
  LOCAL_STORAGE_CONFIG = 'aiems-config-ts'
}

export enum ApplicableAction {
  NOT_AVAILABLE = 'NA',
  ENABLED = 'ENABLED',
  FORBIDDEN = 'FORBIDDEN',
  DISABLED = 'DISABLED'
}

export const ContextActions = {
  EAI: {
    ...EaiSubjectsCA,
    ...EaiUsersCA,
    ...EaiDeliveryPointsCA,
    ...EaiBalanceGroupCA,
    ...EaiDevicesCA,
    ...EaiDeliveryContractsCA,
    ...EaiAccessPageCA
  },
  DISPATCHER: {
    ...DispatcherRkCA,
    ...DispatcherProductionPlanningCA,
    ...DispatcherOperationCalendarCA,
    ...DispatcherAccessPageCA
  },
  REPORTING: {
    ...ReportingCA
  }
};

export enum DateFormat {
  LOG_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss',
  REST_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
  REST_DATE_FORMAT = 'YYYY-MM-DD',
  DATE_FORMAT = 'DD.MM.YYYY',
  BUSINESS_DAY_FORMAT = 'dd.MM.yyyy',
  SHORT_DATE_FORMAT = 'DD.MM',
  DATETIME_FORMAT = 'DD.MM.YYYY HH:mm:ss',
  DATETIME_FORMAT_WITHOUT_SECONDS = 'DD.MM.YYYY HH:mm',
  DATETIME_FORMAT_WITHOUT_SECONDS_EN = 'MM/DD/YYYY HH:mm',
  DATETIME_FORMAT_FOR_DATETIMEPICKER = 'dd.MM.yyyy HH:mm',
  DATETIME_FORMAT_FOR_DATETIMEPICKER_EN = 'MM/dd/yyyy HH:mm',
  DATETIME_COMPARE_FORMAT = 'YYYY-MM-DDTHH:mm',
  CALENDAR_FORMAT_MONTH_YEAR = 'MMMM yyyy',
  CALENDAR_FORMAT_DAY = 'DD MMMM, yyyy',
  CALENDAR_FORMAT_DAY_WITHOUT_MONTH_AND_YEAR = 'DD',
  CALENDAR_FORMAT_DAY_WITHOUT_YEAR = 'DD MMMM',
  CSV_FORMAT = 'YYYYMMDD',
  PERIOD_TIME_FORMAT = 'HH:mm',
  FULL_TIME_FORMAT = 'HH:mm:ss',
  YEAR_MONTH_FORMAT = 'YYYYMM',
  YEAR_MONTH_DATE_FNS = 'MMM yyyy'
}

export enum LayoutType {
  VERTICAL_LAYOUT = 'vertical',
  HORIZONTAL_LAYOUT = 'horizontal',
  DEFAULT_DRAWER = 'default',
  MINI_DRAWER = 'mini-drawer'
}

export const ValidationPatterns = {
  STRING_256_MAX_LENGTH: /^.{1,256}$/,
  PARTY_IDENTIFIER: /^[a-zA-Z0-9/]{1,16}$/,
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  EIC_LENGTH: /^.{16}$/,
  MAX_LENGTH_16_CHARS: /^.{1,16}$/,
  MAX_LENGTH_8_CHARS: /^.{1,8}$/,
  DESCRIPTION_MAX_LENGTH: /^.{0,2000}$/,
  POSTAL_CODE_LENGTH: /^.{5}$/,
  NUMBER_WITH_MAX_TWO_DECIMAL: /^[+-]?\d+[.,]?\d{0,2}$/,
  NUMBER_WITH_MAX_TREE_DECIMAL: /^[+-]?\d+[.,]?\d{0,3}$/,
  NUMBER_WITH_MAX_FIVE_DECIMAL: /^[+-]?\d+[.,]?\d{0,5}$/,
  POSITIVE_INTEGER: /^\d+$/,
  POSITIVE_NUMBER: /^\d*\.?\d+$/,
  INTEGER_NUMBER: /^[+-]?\d+$/,
  MAX_LENGTH_64_ALFANUMERIC_CHARS: /^.{1,64}$/,
  REST_DATE_FORMAT: /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
  LONGITUDE: /^-?([0-9]{1,2}|1[0-7][0-9]|180)(\.[0-9]{1,10})?$/,
  LATITUDE: /^-?([0-8]?[0-9]|90)(\.[0-9]{1,10})?$/,
  GUID: /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/,
  EXPONENTIAL_NUMBER: /^[-+]?[0-9]+(\.[0-9]+)?[eE][-+]?[0-9]+$/
};

export const GRID_SPACING = 3;
export const DRAWER_WIDTH = 288;
export const HORIZONTAL_MAX_ITEMS = 6;

export enum WindowEvents {
  EVENT_SOURCE = 'EventSource'
}

export const DEFAULT_ROWS_PER_PAGE = 10;
export const rowsPerPageOptions: number[] = [10, 20, 50, 100];

export enum UserAccountStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED'
}

export enum EAIEntityTypeCode {
  EMS_PARTY = 'EMS_PARTY',
  USER = 'USER',
  EMS_MEMBER_ACCOUNT = 'EMS_MEMBER_ACCOUNT',
  MARKET_PARTICIPANT = 'MARKET_PARTICIPANT',
  BALANCING_GROUP = 'BALANCING_GROUP',
  DELIVERY_POINT = 'DELIVERY_POINT',
  DEVICE = 'DEVICE',
  DELIVERY_CONTRACT = 'DELIVERY_CONTRACT'
}

export enum DispatcherEntityTypeCode {
  ALARM = 'ALARM',
  OPERATION_CALENDAR = 'OPERATION_CALENDAR',
  PRODUCTION_PLAN = 'PRODUCTION_PLAN',
  DAILY_DIAGRAM = 'DAILY_DIAGRAM',
  SYSTEM = 'SYSTEM',
  USER = 'USER',
  BALANCING_GROUP = 'BALANCING_GROUP',
  RESERVED_CAPACITY_GUARDING_ACTION = 'RESERVED_CAPACITY_GUARDING_ACTION'
}

export enum TableRowCellType {
  ACTIONS = 'actions',
  CHIP = 'chip',
  CHECKBOX = 'checkbox',
  STRING = 'string',
  ELEMENT = 'element',
  LINEAR_PROGRESS_WITH_LABEL = 'linearProgressWithLabel',
  HIDDEN = 'hidden'
}

export enum TableActionType {
  DETAIL = 'detail',
  UPDATE = 'update',
  DELETE = 'delete',
  CREATE = 'create'
}

export enum DeviceInstallationStatus {
  INSTALLED = 'INSTALLED',
  NOT_INSTALLED = 'NOT_INSTALLED'
}

export const MAX_DATE = '3000-01-01';

export enum DispatcherPlanType {
  CONSUMPTION = 'consumption',
  PRODUCTION = 'production'
}

export enum PlanRevisionStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  PROPAGATED = 'PROPAGATED',
  PLANNED = 'PLANNED',
  REJECTED = 'REJECTED',
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED'
}

export enum DiagramRevisionPropagationStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  RETURNED = 'RETURNED'
}

export enum DiagramPeriodApprovalStatus {
  PROPOSED = 'PROPOSED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED'
}

export enum EmsPartyDeletedStatus {
  DELETED = 'DELETED',
  NOT_DELETED = 'NOT_DELETED',
  ALL = 'ALL'
}

export const MAXIMAL_VALID_TO_DATE = '3000-01-01T00:00:00Z';

export const MINIMAL_TIME_INTERVAL = 15;

export enum ChartType {
  BAR = 'bar',
  LINE = 'line',
  AREA = 'area'
}

export enum ChartCurveType {
  SMOOTH = 'smooth',
  STEPLINE = 'stepline',
  STRAIGHT = 'straight'
}

export const GET_LIST_WITHOUT_PAGINATION = '-1';

export enum MetricValueType {
  plannedImbalanceD1 = 'PREDICTION/PRODUCTION/PLANNED_IMBALANCE_D1',
  plannedImbalanceHX = 'PREDICTION/PRODUCTION/PLANNED_IMBALANCE_HX',
  plannedImbalancePrice = 'PREDICTION/ECONOMIC/PLANNED_IMBALANCE_PRICE_D1',
  price15mHX = 'PREDICTION/ECONOMIC/IDM15_PRICE_HX',
  price60mHX = 'PREDICTION/ECONOMIC/IDM60_PRICE_HX',
  price15mD1 = 'PREDICTION/ECONOMIC/IDM15_PRICE_D1',
  price60mD1 = 'PREDICTION/ECONOMIC/IDM60_PRICE_D1',
  predictionSystemImbalanceD1 = 'PREDICTION/SYSTEM/SYSTEM_IMBALANCE_D1',
  predictionSystemImbalanceHX = 'PREDICTION/SYSTEM/SYSTEM_IMBALANCE_HX',
  actualSystemImbalance = 'SYSTEM/SYSTEM_IMBALANCE_REGULAR_DAILY',
  positiveSystemImbalanceProbabilityHX = 'PREDICTION/SYSTEM/POSITIVE_SYSTEM_IMBALANCE_PROBABILITY_HX',
  positiveSystemImbalancePriceHX = 'PREDICTION/ECONOMIC/POSITIVE_SYSTEM_IMBALANCE_PRICE_HX',
  negativeSystemImbalancePriceHX = 'PREDICTION/ECONOMIC/NEGATIVE_SYSTEM_IMBALANCE_PRICE_HX',
  priceDamD1 = 'PREDICTION/ECONOMIC/DAM_PRICE_D1',
  energyActivePower = 'ENERGY/ACTIVE_POWER',
  energyPlannedProduction = 'ENERGY/PLANNED_PRODUCTION',
  energyActiveRequestedPower = 'ENERGY/ACTIVE_POWER_REQUESTED',
  energyReactivePower = 'ENERGY/REACTIVE_POWER',
  infrastructureHeartbeat = 'INFRASTRUCTURE/HEARTBEAT',
  activeEnergyBalance = 'ENERGY/ACTIVE_ENERGY_BALANCE',
  activeEnergySupply = 'ENERGY/ACTIVE_ENERGY_SUPPLY',
  activeEnergyOfftake = 'ENERGY/ACTIVE_ENERGY_OFFTAKE',
  energyMaxPower = 'ENERGY/MAX_POWER_TO_RESERVED_CAPACITY',
  activeEnergyTotalSupply = 'ENERGY/ACTIVE_ENERGY_TOTAL_SUPPLY',
  activeEnergyTotalOfftake = 'ENERGY/ACTIVE_ENERGY_TOTAL_OFFTAKE',
  infrastructureOperationEvent = 'INFRASTRUCTURE/OPERATION_EVENT',
  infrastructureCentreBreakDisconnectorStatus = 'INFRASTRUCTURE/CENTRE_BREAK_DISCONNECTOR_STATUS',
  infrastructureFuseCarrierStatus = 'INFRASTRUCTURE/FUSE_CARRIER_STATUS',
  infrastructureOutputBreakerStatus = 'INFRASTRUCTURE/OUTPUT_BREAKER_STATUS',
  infrastructurePowerSwitchStatus = 'INFRASTRUCTURE/POWER_SWITCH_STATUS',
  infrastructureGroundingStatus = 'INFRASTRUCTURE/GROUNDING_STATUS',
  infrastructureHrmStatus = 'INFRASTRUCTURE/HRM_STATUS',
  infrastructurePhaseElementStatus = 'INFRASTRUCTURE/PHASE_ELEMENT_STATUS',
  infrastructureFveDeviceControlModeP = 'INFRASTRUCTURE/FVE_DEVICE_CONTROL_MODE_P',
  infrastructureMveDeviceControlMode = 'INFRASTRUCTURE/MVE_DEVICE_CONTROL_MODE',
  infrastructureLowerWaterLevel = 'INFRASTRUCTURE/LOWER_WATER_LEVEL',
  infrastructureUpperWaterLevel = 'INFRASTRUCTURE/UPPER_WATER_LEVEL',
  infrastructureWaterFlow = 'INFRASTRUCTURE/WATER_FLOW',
  infrastructureDeviceEfficiency = 'INFRASTRUCTURE/DEVICE_EFFICIENCY',
  infrastructureRequestedPower = 'INFRASTRUCTURE/REQUESTED_POWER',
  infrastructureTankFillLevel = 'INFRASTRUCTURE/TANK_FILL_LEVEL',
  energyAcCurrentP1 = 'ENERGY/AC_CURRENT_PHASE_1',
  energyAcCurrentP2 = 'ENERGY/AC_CURRENT_PHASE_2',
  energyAcCurrentP3 = 'ENERGY/AC_CURRENT_PHASE_3',
  energyAcVoltageP1 = 'ENERGY/AC_VOLTAGE_PHASE_1',
  energyAcVoltageP2 = 'ENERGY/AC_VOLTAGE_PHASE_2',
  energyAcVoltageP3 = 'ENERGY/AC_VOLTAGE_PHASE_3',
  energyCosPhi = 'ENERGY/COS_PHI',
  energyStateOfCharge = 'ENERGY/STATE_OF_CHARGE',
  energyFrequency = 'ENERGY/FREQUENCY',
  energyDCVoltage = 'ENERGY/DC_VOLTAGE',
  energyDCCurrent = 'ENERGY/DC_CURRENT',
  predProdEnergyProdD1 = 'PREDICTION/PRODUCTION/ENERGY_PRODUCTION_D1',
  predProdEnergyProdHX = 'PREDICTION/PRODUCTION/ENERGY_PRODUCTION_HX',
  predProdEndOfPeriodPowerD1 = 'PREDICTION/PRODUCTION/END_OF_PERIOD_POWER_D1',
  predProdEndOfPeriodSocD1 = 'PREDICTION/PRODUCTION/BESS_END_OF_PERIOD_SOC_D1',
  predProdEndOfPeriodSocHX = 'PREDICTION/PRODUCTION/BESS_END_OF_PERIOD_SOC_HX',
  predProdEndOfPeriodEnergyStoredHX = 'PREDICTION/PRODUCTION/BESS_END_OF_PERIOD_ENERGY_STORED_HX',
  predProdEndOfPeriodEnergyStoredD1 = 'PREDICTION/PRODUCTION/BESS_END_OF_PERIOD_ENERGY_STORED_D1',
  predProdEndOfPeriodFreeCapacityD1 = 'PREDICTION/PRODUCTION/BESS_END_OF_PERIOD_FREE_CAPACITY_D1',
  energyProdNomination = 'NOMINATION',
  energyProdSummaryProduction = 'SUMMARY_PRODUCTION',
  currentImbalanceD1 = 'CURRENT_IMBALANCE',
  weatherForecast = 'METEO/WEATHER_FORECAST',
  weatherMeasurement = 'METEO/WEATHER_MEASUREMENT',
  energyImbalance = 'ENERGY/IMBALANCE',
  predProdEnergyOffTake = 'PREDICTION/PRODUCTION/ENERGY_OFFTAKE',
  energyReservedCapacityUtilization = 'ENERGY/RESERVED_CAPACITY_UTILIZATION'
}

export enum MarketPeriodsType {
  MARKET_PERIODS = 'PT15M',
  MARKET_HOURS = 'PT60M'
}

export const MAX_BATTERY_CAPACITY = 100;
export const MIN_BATTERY_CAPACITY = 0;

export enum PlannedImbalanceType {
  D1 = 'D1',
  HX = 'HX'
}

export enum IDM_PRICE_TYPE {
  IDM15_PRICE,
  IDM60_PRICE
}

export const RELOAD_TIME_5_SECONDS = 5000;
export const RELOAD_TIME_30_SECONDS = 30000;
export const RELOAD_TIME_60_SECONDS = 60000;
export const RELOAD_TIME_180_SECONDS = 180000;
export const RELOAD_TIME_2_MINUTES = 120000;

export const statusIconColorMap: AnyParams = {
  '0': 'warning',
  '1': 'error',
  '2': 'success',
  '3': 'error'
};

export const NavItemsForDisplayDeviceEntity = [
  'device-detail',
  'online-dispatching-device'
];
export const NavItemsForDisplayDeliveryPointEntity = [
  'deliveryPoint-detail',
  'online-dispatching-deliveryPoint'
];
export const NavItemsForDisplayUserEntity = ['user-detail'];
export const NavItemsForDisplaySubjectEntity = ['subject-detail'];
export const NavItemsForDisplayBalanceGroupEntity = ['balanceGroup-detail'];
export const NavItemsForDisplayDeliveryContractEntity = [
  'delivery-contract-detail'
];
export const NavItemsForDisplayDeliveryPointGdsEntity = [
  'deliveryPoint-detail-gds-detail'
];

export const FRACTION_DIGITS_THREE_DECIMAL = 3;
export const FRACTION_DIGITS_FIVE_DECIMAL = 5;
export const FRACTION_DIGITS_TWO_DECIMAL = 2;
export const FRACTION_DIGITS_LONELY_DECIMAL = 1;
export const AllowedStatusValues = [0, 1, 2, 3];

export const CELL_WITH_INPUT_SX: KeyedObject = {
  paddingTop: '4px',
  paddingBottom: '2px'
};

export const EIC_LENGTH = 16;
export const PARTY_IDENTIFIER_LENGTH = 16;
export const TIMESERIES_LIST_BATCH_SIZE = 10;

export enum PermissionError {
  NO_ERROR = 'NO_ERROR',
  ACTION_ERROR = 'ACTION_ERROR',
  PAGE_ERROR = 'PAGE_ERROR',
  UNAUTHORIZED_ERROR = 'UNAUTHORIZED_ERROR'
}

export enum SourceQualityFlagsCategories {
  OK = 'OK',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

export const sourceQualityFlagsConfig = [
  { category: SourceQualityFlagsCategories.OK, code: 'OK' },
  { category: SourceQualityFlagsCategories.WARNING, code: 'A' },
  { category: SourceQualityFlagsCategories.WARNING, code: 'SB' },
  { category: SourceQualityFlagsCategories.WARNING, code: 'T' },
  { category: SourceQualityFlagsCategories.WARNING, code: 'M' },
  { category: SourceQualityFlagsCategories.ERROR, code: 'OV' },
  { category: SourceQualityFlagsCategories.ERROR, code: 'NT' },
  { category: SourceQualityFlagsCategories.ERROR, code: 'OB' },
  { category: SourceQualityFlagsCategories.ERROR, code: 'IV' }
];

export enum TimeSeriesType {
  ONLINE_DISPATCHING = 'ONLINE_DISPATCHING',
  PLANNING = 'PLANNING',
  HX = 'HX',
  RESERVED_CAPACITY_GUARDING = 'RESERVED_CAPACITY_GUARDING'
}

export enum ProductionDeviceType {
  FVE = 'FVE',
  BESS = 'BESS',
  MVE = 'MVE',
  KGJ = 'KGJ'
}

export enum ConsumptionDeviceType {
  CONS = 'CONS'
}

export enum DeviceRole {
  PRODUCTION = 'PRODUCTION',
  CONSUMPTION = 'CONSUMPTION'
}

export enum DeliveryPointRole {
  LOCAL_DISTRIBUTION_SYSTEM = 'LOCAL_DISTRIBUTION_SYSTEM'
}

export enum RelatedService {
  PRODUCTION_OPTIMIZATION = 'PRODUCTION_OPTIMIZATION',
  RESERVED_CAPACITY_GUARDING = 'RESERVED_CAPACITY_GUARDING'
}

const ONE_MINUTE_IN_MS = 60 * 1000;
export const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;

export const NBSP = '\u00A0';
